import { Formik, type FormikHelpers } from 'formik'
import { useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { DatePicker } from '@loadsmart/loadsmart-ui'
import { Button, Field, Layout, Text, TextField } from '@loadsmart/miranda-react'
import { type FetchLocationOptions } from '@loadsmart/react-location-select'

import { type Location } from 'core/domain/Location'
import { RequestStatus } from 'core/domain/Request'
import { formatLocationToOption } from 'core/presenters/locationPresenter'
import { LocationSelect } from 'core/ui/components/location/LocationSelect'
import { formatISO } from 'core/utils/date'

import { formatHours } from '../../presenters/locationPresenter'
import { useShipmentAnalytics } from '../hooks/useShipmentAnalytics'
import { useShipmentLocationsContext } from './ShipmentLocationsContext'

type FormValues = {
  location: Location
  date: string
  time: string
}

const CUSTOM_GOOGLE_MAPS_CONFIG: FetchLocationOptions = {
  types: ['cities'],
  restrictions: { country: ['us', 'ca', 'mx'] },
}

const validationSchema = Yup.object().shape({
  location: Yup.object()
    .shape({
      city: Yup.string().required(),
      state: Yup.string().required(),
      lat: Yup.string().required(),
      lng: Yup.string().required(),
    })
    .required(),
  date: Yup.string().required(),
  time: Yup.string().required(),
})

function getInitialValues(currentDate: Date) {
  return {
    location: {},
    date: formatISO(currentDate),
    time: formatHours(currentDate),
  }
}

type DriverLocationFormProps = {
  readonly shipmentUUID: string
}

export default function DriverLocationForm({ shipmentUUID }: DriverLocationFormProps) {
  let currentDate = new Date()

  const initialValues = getInitialValues(currentDate)

  const { track } = useShipmentAnalytics()

  const { addLocationStatus, refetchLocations, addLocation } = useShipmentLocationsContext()

  const handleSubmit = useCallback(
    async ({ date, location, time }: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
      await addLocation({
        shipmentUUID,
        city: location.city ?? '',
        state: location.state ?? '',
        latitude: String(location.lat),
        longitude: String(location.lng),
        date,
        time,
      })
      await refetchLocations()
      formikHelpers.setFieldValue('location', {})
    },
    [addLocation, refetchLocations, shipmentUUID]
  )

  useEffect(() => {
    if (addLocationStatus === RequestStatus.Done) {
      toast.success('Location added successfully')
      track('Shipment Details / Agnostic / Manual location')
    }
  }, [addLocationStatus, track])

  useEffect(() => {
    if (addLocationStatus === RequestStatus.Error) {
      toast.error('Failed to add location, try again later')
    }
  }, [addLocationStatus])

  return (
    <Layout.Stack gap="spacing-2">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({
          values,
          isValid,
          isSubmitting,
          setFieldValue,
          submitForm,
          handleChange,
          handleBlur,
        }) => {
          const canSubmit = isValid && !isSubmitting

          return (
            <form>
              <Layout.Stack gap="spacing-2">
                <Text variant="body-sm-bold" color="color-text-primary">
                  Add a manual location ping
                </Text>
                <Field style={{ flex: '1' }}>
                  <Field.Label id="last-location-label">Last location</Field.Label>
                  <LocationSelect<Location>
                    aria-labelledby="last-location-label"
                    id="last-location"
                    name="location"
                    placeholder="Enter city name"
                    value={values.location}
                    config={CUSTOM_GOOGLE_MAPS_CONFIG}
                    mapLocationToValue={location => location}
                    mapValueToOption={() => formatLocationToOption(values.location, false)}
                    onChange={newLocation => setFieldValue('location', newLocation)}
                    onBlur={handleBlur}
                  />
                </Field>

                <Layout.Group align="flex-end">
                  <Field>
                    <Field.Label id="date-label">Date</Field.Label>
                    <DatePicker
                      getInputProps={() => ({
                        'aria-labelledby': 'date-label',
                      })}
                      id="date"
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                    />
                  </Field>

                  <Field>
                    <Field.Label id="time-label">Time</Field.Label>
                    <TextField
                      aria-labelledby="time-label"
                      id="time"
                      name="time"
                      type="time"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.time}
                    />
                  </Field>

                  <Button
                    type="button"
                    variant="primary"
                    disabled={!canSubmit}
                    onClick={submitForm}
                  >
                    Share location
                  </Button>
                </Layout.Group>
              </Layout.Stack>
            </form>
          )
        }}
      </Formik>
      <Text variant="body-sm" color="color-text-primary">
        NOTICE: Set date and time to match driver's timezone.
      </Text>
    </Layout.Stack>
  )
}
